import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import { getQualificationResponses } from '../utils/qualificationResponses';
import { formatApiDetail } from '../utils/apiError';
import { capitalizeWords, isStrictEmail, normalizeEmail } from '../utils/formInputUtils';
import LegalModal from '../components/LegalModal';
import LanguageSwitch from '../components/LanguageSwitch';
import AppHeader from '../components/AppHeader';
import './Registration.css';
import './SsoSignupFlow.css';

export default function Registration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openLegal, setOpenLegal] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [countryOfOrigin, setCountryOfOrigin] = useState('');
  const [applicantType, setApplicantType] = useState(null);
  const [familyType, setFamilyType] = useState(null);
  const [familyCount, setFamilyCount] = useState(2);
  const [useEmailAsMain, setUseEmailAsMain] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [hasTriedContinue, setHasTriedContinue] = useState(false);
  const formRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const applicantTypeRef = useRef(null);
  const familyTypeRef = useRef(null);

  const choicesApplicant = [
    { value: 'me', label: t('ssoSignup.forMe'), icon: 'ri-user-3-line' },
    { value: 'someone_else', label: t('ssoSignup.forSomeoneElse'), icon: 'ri-user-shared-2-line' },
  ];
  const choicesFamily = [
    { value: 'just_me', label: t('ssoSignup.justMe'), icon: 'ri-user-line' },
    { value: 'with_family', label: t('ssoSignup.forMyFamily'), icon: 'ri-group-line' },
  ];
  const hasInvalidEmail = Boolean(normalizeEmail(email) && !isStrictEmail(email));
  const isContinueDisabled =
    loading || !firstName.trim() || !lastName.trim() || !applicantType || !familyType || hasInvalidEmail;

  const focusAndScroll = (target) => {
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      target.focus({ preventScroll: true });
    }, 200);
  };

  const getFirstInvalidField = () => {
    if (!firstName.trim()) return firstNameRef.current;
    if (!lastName.trim()) return lastNameRef.current;
    if (!applicantType) return applicantTypeRef.current;
    if (!familyType) return familyTypeRef.current;
    if (hasInvalidEmail) return emailRef.current;
    return null;
  };

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('userData') || '{}');
      setFirstName(stored.first_name || stored.firstName || '');
      setLastName(stored.last_name || stored.lastName || '');
      setEmail(stored.email || '');
      setCountryOfOrigin(stored.country_of_origin || '');
      setApplicantType(
        stored.applicant_type === 'self'
          ? 'me'
          : stored.applicant_type === 'others'
            ? 'someone_else'
            : null
      );
      setFamilyType(
        stored.application_type === 'individual'
          ? 'just_me'
          : stored.application_type === 'family'
            ? 'with_family'
            : null
      );
      setFamilyCount(Number(stored.family_count) >= 2 ? Number(stored.family_count) : 2);
      setUseEmailAsMain(Boolean(stored.email_primary));
      if (stored.date_of_birth) {
        const d = new Date(stored.date_of_birth);
        if (!Number.isNaN(d.getTime())) setDateOfBirth(d.toISOString().slice(0, 10));
      }
    } catch {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasTriedContinue(true);
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('ssoSignup.errors.nameRequired', 'First and last name are required.'));
      focusAndScroll(getFirstInvalidField());
      return;
    }
    if (!applicantType) {
      setError(t('ssoSignup.errors.forRequired', 'Select who this application is for.'));
      focusAndScroll(getFirstInvalidField());
      return;
    }
    if (!familyType) {
      setError(t('ssoSignup.errors.familyRequired', 'Select whether you are applying alone or with family.'));
      focusAndScroll(getFirstInvalidField());
      return;
    }
    const normalizedEmail = normalizeEmail(email);
    if (normalizedEmail && !isStrictEmail(normalizedEmail)) {
      setEmailError(t('ssoSignup.errors.invalidEmail', 'Please enter a valid email address.'));
      focusAndScroll(getFirstInvalidField());
      return;
    }
    setLoading(true);
    try {
      const qualificationResponses = getQualificationResponses();
      const res = await mvpApi.updatePersonalDetails({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: normalizedEmail || null,
        date_of_birth: dateOfBirth || null,
        country_of_origin: countryOfOrigin || null,
        applicant_type: applicantType === 'me' ? 'self' : 'others',
        application_type: familyType === 'just_me' ? 'individual' : 'family',
        family_count: familyType === 'with_family' ? familyCount : null,
        email_primary: useEmailAsMain,
        consent_updates: true,
        qualificationResponses: qualificationResponses || null,
      });
      const user = res.data?.user;
      if (user) localStorage.setItem('userData', JSON.stringify(user));
      navigate('/es/regularizacion/success');
    } catch (err) {
      const fromApi = formatApiDetail(err.response?.data?.detail);
      setError(fromApi || err.message || t('ssoSignup.errors.saveFailed', 'Could not save. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const getEnterNavigationTargets = () => {
    const root = formRef.current;
    if (!root) return [];
    const nodes = Array.from(root.querySelectorAll(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
    ));
    return nodes.filter((el) => {
      if (el.disabled) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      if (el.tabIndex < 0) return false;
      return true;
    });
  };

  const handleEnterAdvance = (e) => {
    if (e.key !== 'Enter') return;
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.target instanceof HTMLTextAreaElement) return;

    const targets = getEnterNavigationTargets();
    const idx = targets.indexOf(e.target);
    if (idx === -1) return;

    e.preventDefault();
    const next = targets[idx + 1];
    if (next) {
      next.focus();
      return;
    }

    // Last control: preserve existing form submit action.
    formRef.current?.requestSubmit();
  };

  const handleDisabledContinueAttempt = () => {
    if (!isContinueDisabled) return;
    setHasTriedContinue(true);
    focusAndScroll(getFirstInvalidField());
  };

  return (
    <div className="reg-page">
      <AppHeader rightSlot={<LanguageSwitch />} />

      <main className="reg-main">
        <div className="reg-progress">
          <div />
          <div />
        </div>
        <div className="reg-step-row">
          <span>{t('ssoSignup.step2of2')}</span>
          <span className="reg-time">{t('ssoSignup.takesLessThanMinute')}</span>
        </div>
        <h1 className="sso-title">
          <Trans i18nKey="ssoSignup.detailsTitle" components={{ accent: <span className="sso-title-accent" /> }} />
        </h1>
        <p className="sso-subdesc">{t('ssoSignup.detailsSubtitle')}</p>

        <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleEnterAdvance} className="reg-form">
          <div className="reg-grid-2">
            <div>
              <label className="reg-label">{t('ssoSignup.firstName')}</label>
              <input
                ref={firstNameRef}
                type="text"
                className={`reg-input ${hasTriedContinue && !firstName.trim() ? 'reg-input--invalid' : ''}`}
                placeholder=""
                value={firstName}
                onChange={(e) => setFirstName(capitalizeWords(e.target.value))}
                required
              />
            </div>
            <div>
              <label className="reg-label">{t('ssoSignup.lastName')}</label>
              <input
                ref={lastNameRef}
                type="text"
                className={`reg-input ${hasTriedContinue && !lastName.trim() ? 'reg-input--invalid' : ''}`}
                placeholder=""
                value={lastName}
                onChange={(e) => setLastName(capitalizeWords(e.target.value))}
                required
              />
            </div>
          </div>

          <div>
            <label className="reg-label">
              {t('ssoSignup.verifyEmailOptional')} <span className="reg-label-optional">{t('ssoSignup.optional')}</span>
            </label>
            <input
              ref={emailRef}
              type="email"
              className={`reg-input ${hasInvalidEmail ? 'reg-input--invalid' : ''}`}
              placeholder=""
              value={email}
              onChange={(e) => {
                const next = e.target.value;
                setEmail(next);
                const normalized = normalizeEmail(next);
                setEmailError(normalized && !isStrictEmail(normalized) ? t('ssoSignup.errors.invalidEmail', 'Please enter a valid email address.') : '');
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData('text') || '';
                const next = `${email}${pasted}`;
                const normalized = normalizeEmail(next);
                setEmailError(normalized && !isStrictEmail(normalized) ? t('ssoSignup.errors.invalidEmail', 'Please enter a valid email address.') : '');
              }}
            />
            {emailError ? <p className="reg-error">{emailError}</p> : null}
            <p className="reg-help">{t('ssoSignup.updatesOrDocs')}</p>
            {email.trim().length > 0 && (
              <button
                type="button"
                className="reg-checkbox-row"
                onClick={() => setUseEmailAsMain((v) => !v)}
              >
                <span className={`reg-checkbox ${useEmailAsMain ? 'checked' : ''}`} />
                <span>{t('ssoSignup.emailAsMain')}</span>
              </button>
            )}
          </div>

          <div className="reg-divider" />

          <div>
            <p className="reg-group-title">{t('ssoSignup.forQuestion')}</p>
            <div className="reg-choice-row">
              {choicesApplicant.map(({ value, label, icon }) => {
                const active = applicantType === value;
                return (
                  <button
                    ref={value === 'me' ? applicantTypeRef : null}
                    key={value}
                    type="button"
                    className={`sso-choice-btn ${active ? 'sso-choice-btn--active' : ''}`}
                    onClick={() => setApplicantType(value)}
                  >
                    <span className="sso-choice-icon">
                      <i className={icon} aria-hidden />
                    </span>
                    <span>{label}</span>
                    {active && (
                      <span className="sso-choice-check">
                        <i className="ri-check-line" aria-hidden />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="reg-divider" />

          <div>
            <p className="reg-group-title">{t('ssoSignup.familyQuestion')}</p>
            <div className="reg-choice-row">
              {choicesFamily.map(({ value, label, icon }) => {
                const active = familyType === value;
                return (
                  <button
                    ref={value === 'just_me' ? familyTypeRef : null}
                    key={value}
                    type="button"
                    className={`sso-choice-btn ${active ? 'sso-choice-btn--active' : ''}`}
                    onClick={() => setFamilyType(value)}
                  >
                    <span className="sso-choice-icon">
                      <i className={icon} aria-hidden />
                    </span>
                    <span>{label}</span>
                    {active && (
                      <span className="sso-choice-check">
                        <i className="ri-check-line" aria-hidden />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {familyType === 'with_family' && (
              <div className="reg-family-count sso-family-count">
                <div>
                  <p className="reg-family-title">{t('ssoSignup.howManyPeople')}</p>
                  <p className="reg-family-sub">{t('ssoSignup.includingYourself')}</p>
                </div>
                <div className="reg-count-controls">
                  <button type="button" onClick={() => setFamilyCount((n) => Math.max(2, n - 1))}>-</button>
                  <span>{familyCount}</span>
                  <button type="button" onClick={() => setFamilyCount((n) => Math.min(12, n + 1))}>+</button>
                </div>
              </div>
            )}
          </div>

          {/* <div className="reg-divider" /> */}

          {error && <p className="reg-error">{error}</p>}
          <div className="reg-btn-primary-wrap" onClick={handleDisabledContinueAttempt}>
            <button
              type="submit"
              className="btn-primary reg-continue-btn"
              disabled={isContinueDisabled}
            >
              {loading ? t('ssoSignup.saving') : t('ssoSignup.continue')}
            </button>
          </div>
          <p className="reg-details-terms">
            <Trans
              i18nKey="ssoSignup.detailsContinueTerms"
              components={{
                terms: (
                  <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('terms')} />
                ),
                privacy: (
                  <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('privacy')} />
                ),
                dpa: <button type="button" className="reg-details-terms-link" onClick={() => setOpenLegal('data')} />,
              }}
            />
          </p>
        </form>
      </main>

      <LegalModal type={openLegal || 'terms'} open={openLegal !== null} onClose={() => setOpenLegal(null)} />
    </div>
  );
}
